import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, User, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VettingNewCase() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    subject_name: "",
    subject_id_type: "id_number",
    subject_id_value: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.subject_name.trim() || !form.subject_id_value.trim()) {
      setError("Subject name and ID value are required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/vetting/cases`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      const caseId = res.data.case?.id;
      if (caseId) {
        navigate(`/vetting/${caseId}`);
      } else {
        navigate("/vetting");
      }
    } catch (e) {
      console.error("Create case error:", e.response?.data || e.message);
      setError(e.response?.data?.detail || "Failed to create case");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/vetting")}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Cases
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] tracking-wide">
              NEW VETTING CASE
            </h1>
            <p className="text-sm text-slate-500">Enter the subject's details to begin</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject Full Name</label>
            <input
              type="text"
              value={form.subject_name}
              onChange={e => setForm({ ...form, subject_name: e.target.value })}
              placeholder="e.g. Thabo Mbeki"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">ID Type</label>
            <select
              value={form.subject_id_type}
              onChange={e => setForm({ ...form, subject_id_type: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <option value="id_number">National ID Number</option>
              <option value="passport">Passport Number</option>
              <option value="business_reg">Business Registration</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">ID Value</label>
            <input
              type="text"
              value={form.subject_id_value}
              onChange={e => setForm({ ...form, subject_id_value: e.target.value })}
              placeholder="e.g. 860101 5800 081"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              required
            />
          </div>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/vetting")}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-app"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              {submitting ? "Creating..." : "Create Case"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
